import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Alert, Box, Button, Chip, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ProtocolsContext } from '../../../context/protocolsContext';
import { ReviewSessionsContext } from '../../../context/review/ReviewSessionsContext';
import {
    ActivationOptions,
    AreaStateType,
    EnergyLevelOptions,
    ProtocolType,
    SessionType,
    ageOptions,
    laserCrystalUsedOptions,
    remoteOptions,
    yesNoOptions,
} from '../../../types';
import MetaField from '../../common/MetaField';
import { getProtocolLabel } from '../../SessionsTab/EditSessionDialog';
import { formatSessionDateLong } from './sessionDisplay';

interface SessionDetailPanelProps {
    session: SessionType;
    index: number;
    total: number;
    onLoaded?: (sessionId: number) => void;
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Box component='section' sx={{ py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1.5 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const scanTone = (value: number) => {
    if (value === 12) return 'success' as const;
    if (value === 0) return 'default' as const;
    return 'warning' as const;
};

const AreaStateCard = ({ state }: { state: AreaStateType }) => (
    <Box
        sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, overflowWrap: 'anywhere' }}
    >
        <Typography variant='caption' sx={{ fontWeight: 700 }}>
            {state.area?.name ?? '—'}
        </Typography>
        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
            <Chip
                size='small'
                variant='outlined'
                color={scanTone(state.activation)}
                sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                label={`Activation: ${ActivationOptions[state.activation] ?? 'Not recorded'}`}
            />
            <Chip
                size='small'
                variant='outlined'
                color={scanTone(state.energy_level)}
                sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                label={`Energy: ${EnergyLevelOptions[state.energy_level] ?? 'Not recorded'}`}
            />
        </Stack>
    </Box>
);

const AreaStateGrid = ({ title, states }: { title: string; states: AreaStateType[] }) => (
    <Box>
        <Typography variant='overline' sx={{ fontWeight: 700, color: 'text.secondary' }}>
            {title}
        </Typography>
        {states.length === 0 ? (
            <Typography variant='body2' color='text.disabled'>
                Not scanned
            </Typography>
        ) : (
            <Grid container spacing={1} sx={{ mt: 0 }}>
                {states.map((state) => (
                    <Grid item xs={12} sm={6} key={state.id}>
                        <AreaStateCard state={state} />
                    </Grid>
                ))}
            </Grid>
        )}
    </Box>
);

const DetailSkeleton = () => (
    <Stack spacing={2}>
        <Skeleton variant='rounded' height={96} />
        <Skeleton variant='rounded' height={180} />
        <Skeleton variant='rounded' height={220} />
    </Stack>
);

const SessionDetailPanel = ({ session, index, total, onLoaded }: SessionDetailPanelProps) => {
    const { sessionDetails, getSessionDetail } = useContext(ReviewSessionsContext);
    const { protocols } = useContext(ProtocolsContext);
    const [error, setError] = useState<{ sessionId: number; message: string } | null>(null);
    const [retryToken, setRetryToken] = useState(0);

    const detail = sessionDetails[session.id];

    useEffect(() => {
        if (detail) onLoaded?.(session.id);
    }, [detail, session.id, onLoaded]);

    useEffect(() => {
        if (detail) return undefined;
        let active = true;
        setError(null);
        getSessionDetail(session.id).catch(() => {
            if (active) setError({ sessionId: session.id, message: 'This session could not be loaded.' });
        });
        return () => {
            active = false;
        };
    }, [detail, getSessionDetail, session.id, retryToken]);

    const areaStates = useMemo(() => {
        const preSession = (detail?.areas_states ?? []).filter((state) => state.type === 'PRE_SESSION');
        return {
            major: preSession.filter((state) => state.area?.major),
            minor: preSession.filter((state) => !state.area?.major),
        };
    }, [detail]);

    const protocolLabels = useMemo(() => {
        if (!detail) return '';
        return detail.protocols
            .map((sessionProtocol) => {
                const full = protocols.find((p: ProtocolType) => p.id === sessionProtocol.id);
                return full ? getProtocolLabel(full) : sessionProtocol.name;
            })
            .join(', ');
    }, [detail, protocols]);

    if (!detail && error?.sessionId === session.id) {
        return (
            <Alert
                severity='error'
                action={
                    <Button
                        color='inherit'
                        size='small'
                        startIcon={<RefreshRoundedIcon />}
                        onClick={() => {
                            setError(null);
                            setRetryToken((token) => token + 1);
                        }}
                    >
                        Retry
                    </Button>
                }
            >
                {error.message}
            </Alert>
        );
    }

    if (!detail) {
        return <DetailSkeleton />;
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant='overline' sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {`Session ${index + 1} of ${total}`}
                </Typography>
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {formatSessionDateLong(detail.date)}
                </Typography>
                <Stack direction='row' spacing={1} sx={{ mt: 1 }} flexWrap='wrap' useFlexGap>
                    <Chip size='small' label={`#${detail.id}`} variant='outlined' />
                    <Chip size='small' label={remoteOptions[detail.remote ?? 'NO_ANSWER'] ?? 'No Answer'} variant='outlined' />
                    <Chip
                        size='small'
                        label={laserCrystalUsedOptions[detail.crystal_used ?? 'NO_ANSWER'] ?? 'No Answer'}
                        variant='outlined'
                    />
                </Stack>
            </Box>

            <Section title='Client'>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <MetaField label='Age range' value={ageOptions[detail.client?.age_range ?? '']} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <MetaField
                            label='Possibly pregnant'
                            value={yesNoOptions[detail.client?.possibly_pregnant ?? 'NO_ANSWER']}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <MetaField
                            label='High blood pressure'
                            value={yesNoOptions[detail.client?.high_blood_pressure_diagnosed ?? 'NO_ANSWER']}
                        />
                    </Grid>
                </Grid>
            </Section>

            <Section title='Before the session'>
                <Stack spacing={2}>
                    <MetaField label='Previous session notes' value={detail.previous_session_notes} block />
                    <Divider />
                    <AreaStateGrid title='Major chakras' states={areaStates.major} />
                    <AreaStateGrid title='Minor chakras' states={areaStates.minor} />
                    <Divider />
                    <MetaField label='Other observations' value={detail.pre_session_notes} block />
                </Stack>
            </Section>

            <Section title='The session'>
                <Stack spacing={2}>
                    <MetaField label='Protocols used' value={protocolLabels} />
                    <MetaField label='Protocol notes' value={detail.protocol_notes} block />
                </Stack>
            </Section>

            <Section title='After the session'>
                <Stack spacing={2}>
                    <MetaField
                        label='Recommendations'
                        value={detail.recommendations.map((recommendation) => recommendation.name).join(', ')}
                    />
                    <MetaField label='Post session notes' value={detail.post_session_client_notes} block />
                </Stack>
            </Section>
        </Stack>
    );
};

export default SessionDetailPanel;
